/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import * as DT from './DataTypes';
import MatSDK from '../WasmModules/MatSDK';
import {
  createBufferGeometry,
  createBufferGeometryFromLineSegments,
  createBufferGeometryFromPolyline,
  createBufferGeometryFromPolylinesVector,
  createTriangleSurface,
  emmanaged
} from './MatSDKHelpers';
import {
  centroid,
  contour2dParams,
  coronalOrientationFromDirection,
  createBufferGeometryFromPointsCollection,
  inscribedSectors,
  loadDracoAsBufferGeometry,
  makeTransformation,
  unitNormal
} from './Helpers';
import * as THREE from 'three';
import type { IWrapper } from '@/engine/DataFlow/DataTypes';
import type { Instrumentation } from '../Instrumentation/Interfaces';
import { InstrumentationModelsProvider } from '../Instrumentation/InstrumentationModelsProvider';
import { InstrumentationLoader } from '../Instrumentation/InstrumentationLoader';
import { MakeAsyncFunctor, MakeFunctor } from '@/engine/DataFlow/Functor';
import type { IValue } from '@/engine/DataFlow/Graph';
import { CatmullRomCurve3Type, getCatmullRomCurve3Points } from '../utils/geometryHelper';

function _isValueInCriteriaRange(value: number, range: DT.CriteriaRangeT): boolean {
  if (range.greaterThan !== undefined && value <= range.greaterThan) {
    return false;
  }
  if (range.lessThan !== undefined && value >= range.lessThan) {
    return false;
  }
  return true;
}

export function _makeCheckCriteria(
  value: number,
  defaultStatus: DT.CheckStatusT,
  yesRange?: DT.CriteriaRangeT,
  maybeRange?: DT.CriteriaRangeT,
  noRange?: DT.CriteriaRangeT
): DT.CheckCriteriaT {
  if (
    (defaultStatus === DT.CheckStatusT.Yes && yesRange !== undefined) ||
    (defaultStatus === DT.CheckStatusT.Maybe && maybeRange !== undefined) ||
    (defaultStatus === DT.CheckStatusT.No && noRange !== undefined)
  ) {
    throw new Error('Invalid default status');
  }

  const yes = yesRange ? _isValueInCriteriaRange(value, yesRange) : undefined;
  const maybe = maybeRange ? _isValueInCriteriaRange(value, maybeRange) : undefined;
  const no = noRange ? _isValueInCriteriaRange(value, noRange) : undefined;
  if (yes === undefined && maybe === undefined && no === undefined) {
    throw new Error('Invalid criteria');
  }

  let status: DT.CheckStatusT | undefined = undefined;
  if (yes === true) {
    if (no === true || maybe === true) {
      throw new Error('Invalid criteria');
    }
    status = DT.CheckStatusT.Yes;
  } else if (maybe === true) {
    if (no === true) {
      throw new Error('Invalid criteria');
    }
    status = DT.CheckStatusT.Maybe;
  } else if (no === true) {
    status = DT.CheckStatusT.No;
  }

  return {
    value: value,
    defaultStatus: defaultStatus,
    status: status ?? defaultStatus,
    yesRange: yesRange,
    maybeRange: maybeRange,
    noRange: noRange
  };
}

export const tuneValue = MakeFunctor(
  'FT.tuneValue',
  <T, U extends IValue & IWrapper<T>>(dflt: U, user: U, appr: U, flag: DT.TuneFlag): U => {
    switch (flag.value) {
      case DT.ETuneFlag.DEFAULT:
        return dflt;
      case DT.ETuneFlag.USER:
        return user;
      case DT.ETuneFlag.APPROVED:
        return appr;
      default:
        throw new Error('Unknown');
    }
  }
);

export const ensureNotDefaultValue = MakeFunctor(
  'FT.ensureNotDefaultValue',
  <T, U extends IValue & IWrapper<T>>(value: U, flag: DT.TuneFlag): U | undefined => {
    if (flag.value == DT.ETuneFlag.DEFAULT) return undefined;
    return value;
  }
);

export const distancePointToPlane = MakeFunctor('FT.distancePointToPlane', (pt: DT.Point, pl: DT.Plane): DT.NumberT => {
  return new DT.NumberT(pl.value.planeEq.distanceToPoint(pt.value));
});

export const distancePointToPoint = MakeFunctor(
  'FT.distancePointToPoint',
  (pt1: DT.Point, pt2: DT.Point): DT.NumberT => {
    return new DT.NumberT(pt1.value.distanceTo(pt2.value));
  }
);

export const lengthOfTheLine = MakeFunctor('FT.lengthOfTheLine', (ln: DT.Line): DT.NumberT => {
  return new DT.NumberT(ln.value.distance());
});

export const startOfTheLine = MakeFunctor('FT.startOfTheLine', (ln: DT.Line): DT.Point => {
  return new DT.Point(ln.value.start.clone());
});

export const endOfTheLine = MakeFunctor('FT.endOfTheLine', (ln: DT.Line): DT.Point => {
  return new DT.Point(ln.value.end.clone());
});

export const lengthOfTheLineSigned = MakeFunctor(
  'FT.lengthOfTheLineSigned',
  (ln: DT.Line, dir: DT.Vector): DT.NumberT => {
    let dist = ln.value.distance();
    if (ln.value.delta(new THREE.Vector3()).dot(dir.value) < 0) {
      dist = -dist;
    }
    return new DT.NumberT(dist);
  }
);

export const areaOfTheMesh = MakeFunctor('FT.areaOfTheMesh', (surf: DT.MatTriangleSurface): DT.NumberT => {
  return new DT.NumberT(surf.value.getArea());
});

export const volumeOfTheMesh = MakeFunctor('FT.volumeOfTheMesh', (surf: DT.MatTriangleSurface): DT.NumberT => {
  return new DT.NumberT(surf.value.getVolume());
});

export const barycenterOfTheMesh = MakeFunctor('FT.barycenterOfTheMesh', (surf: DT.MatTriangleSurface): DT.Point => {
  return emmanaged(surf.value.getBarycenter(), (pt) => new DT.Point(new THREE.Vector3(pt.x, pt.y, pt.z)));
});

export const sumUp = MakeFunctor('FT.sumUp', (...args: Array<DT.NumberT>): DT.NumberT => {
  let sum = 0;
  args.forEach((element) => (sum += element.value));
  return new DT.NumberT(sum);
});

export function areaMeasurementParams(
  basePts: Array<THREE.Vector3>,
  isClosed: boolean,
  numSectorsInQuarter: number,
  dirZ?: THREE.Vector3,
  dirY?: THREE.Vector3,
  centerPt?: THREE.Vector3
): DT.IAreaMeasurementParams | undefined {
  if (basePts.length == 0) {
    return undefined;
  }

  const origin = centroid(basePts);
  const orientationZ = unitNormal(origin, basePts, isClosed).normalize();
  if (dirZ && orientationZ.dot(dirZ) < 0) {
    orientationZ.negate();
  }

  const orientationY = new THREE.Vector3();
  const from2dTo3d = new THREE.Matrix4();
  if (dirY) {
    orientationY.copy(dirY).cross(orientationZ).negate().cross(orientationZ).normalize();
    const orientationX = orientationY.clone().cross(orientationZ).normalize();
    from2dTo3d.copy(makeTransformation(origin, orientationX, orientationY, orientationZ));
  } else {
    if (!dirZ) {
      if (Math.abs(orientationZ.x) > Math.abs(orientationZ.y) && Math.abs(orientationZ.x) > Math.abs(orientationZ.z)) {
        orientationZ.negate();
        orientationY.set(0, 0, -1).cross(orientationZ).negate().cross(orientationZ).normalize();
      } else if (Math.abs(orientationZ.y) > Math.abs(orientationZ.z)) {
        orientationY.set(0, 0, 1).cross(orientationZ).negate().cross(orientationZ).normalize();
      } else {
        if (orientationZ.z < 0) {
          orientationZ.negate();
        }
        orientationY.set(0, 1, 0).cross(orientationZ).negate().cross(orientationZ).normalize();
      }
      const orientationX = orientationY.clone().cross(orientationZ).normalize();
      from2dTo3d.copy(makeTransformation(origin, orientationX, orientationY, orientationZ));
    } else {
      const orientation = coronalOrientationFromDirection(orientationZ);
      const vecX = orientation.orientationY.clone().cross(orientation.orientationZ).normalize();
      from2dTo3d.copy(makeTransformation(origin, vecX, orientation.orientationY, orientation.orientationZ));
    }
  }
  const from3dTo2d = from2dTo3d.clone().invert();

  const basePtsProj = new Array<THREE.Vector3>(basePts.length);
  for (let i = 0; i < basePts.length; ++i) {
    basePtsProj[i] = basePts[i].clone().applyMatrix4(from3dTo2d).setComponent(2, 0).applyMatrix4(from2dTo3d);
  }

  const splinePts = getCatmullRomCurve3Points(basePtsProj, isClosed, 5, CatmullRomCurve3Type.Centripetal);

  const polyline2d = new Array<THREE.Vector2>(splinePts.length);
  for (let i = 0; i < splinePts.length; ++i) {
    polyline2d[i] = new THREE.Vector2().fromArray(splinePts[i].clone().applyMatrix4(from3dTo2d).toArray());
  }

  const params = contour2dParams(polyline2d);

  const center2d = centerPt
    ? new THREE.Vector2().fromArray(centerPt.clone().applyMatrix4(from3dTo2d).toArray())
    : params.center;

  const sectors = inscribedSectors(polyline2d, center2d, numSectorsInQuarter);

  const area = Math.abs(params.signedArea);
  const perimeter = params.perimeter;
  const dA = 2 * Math.sqrt(area / Math.PI);
  const dP = perimeter / Math.PI;
  const center3d = new THREE.Vector3(center2d.x, center2d.y, 0).applyMatrix4(from2dTo3d);

  if (sectors.length == 0) {
    return {
      basePts: basePtsProj,
      splinePts: splinePts,
      area: area,
      perimeter: perimeter,
      dA: dA,
      dP: dP,
      cs: from2dTo3d.setPosition(center3d)
    };
  } else {
    const sectorsInHalf = sectors.length / 2;
    const sectorsInQuarter = sectors.length / 4;
    // find smallest and largest diameters
    let dMinIdx = -1,
      dMaxIdx = -1;
    let dMin = -1,
      dMax = -1,
      dMean = 0;
    for (let i = 0; i < sectorsInHalf; ++i) {
      const pt1 = sectors[i];
      const pt2 = sectors[i + sectorsInHalf];
      const len = Math.sqrt((pt2.x - pt1.x) * (pt2.x - pt1.x) + (pt2.y - pt1.y) * (pt2.y - pt1.y));
      if (dMin < 0 || dMin > len) {
        dMinIdx = i;
        dMin = len;
      }
      if (dMax < 0 || dMax < len) {
        dMaxIdx = i;
        dMax = len;
      }
      dMean += len;
    }
    dMean /= sectorsInHalf;
    // perpendicular to largest diameter
    const dPerpIdx1 = dMaxIdx + sectorsInQuarter;
    const dPerpIdx2 = dPerpIdx1 < sectorsInHalf ? dPerpIdx1 + sectorsInHalf : dPerpIdx1 - sectorsInHalf;
    // 2d points
    const ptMin1 = sectors[dMinIdx];
    const ptMin2 = sectors[dMinIdx + sectorsInHalf];
    const ptMax1 = sectors[dMaxIdx];
    const ptMax2 = sectors[dMaxIdx + sectorsInHalf];
    const ptPerp1 = sectors[dPerpIdx1];
    const ptPerp2 = sectors[dPerpIdx2];
    const ptX1 = sectors[0];
    const ptX2 = sectors[sectorsInHalf];
    const ptY1 = sectors[sectorsInQuarter];
    const ptY2 = sectors[sectorsInQuarter + sectorsInHalf];
    // results
    const lMin = new THREE.Line3(
      new THREE.Vector3(ptMin1.x, ptMin1.y, 0),
      new THREE.Vector3(ptMin2.x, ptMin2.y, 0)
    ).applyMatrix4(from2dTo3d);
    const lMax = new THREE.Line3(
      new THREE.Vector3(ptMax1.x, ptMax1.y, 0),
      new THREE.Vector3(ptMax2.x, ptMax2.y, 0)
    ).applyMatrix4(from2dTo3d);
    const lPerp = new THREE.Line3(
      new THREE.Vector3(ptPerp1.x, ptPerp1.y, 0),
      new THREE.Vector3(ptPerp2.x, ptPerp2.y, 0)
    ).applyMatrix4(from2dTo3d);
    const lX = new THREE.Line3(new THREE.Vector3(ptX1.x, ptX1.y, 0), new THREE.Vector3(ptX2.x, ptX2.y, 0)).applyMatrix4(
      from2dTo3d
    );
    const lY = new THREE.Line3(new THREE.Vector3(ptY1.x, ptY1.y, 0), new THREE.Vector3(ptY2.x, ptY2.y, 0)).applyMatrix4(
      from2dTo3d
    );
    return {
      basePts: basePtsProj,
      splinePts: splinePts,
      area: area,
      perimeter: perimeter,
      dA: dA,
      dP: dP,
      cs: from2dTo3d.setPosition(center3d),
      lMin: lMin,
      lMax: lMax,
      lPerp: lPerp,
      dMean: dMean,
      lX: lX,
      lY: lY
    };
  }
}

export const areaMeasurement = MakeFunctor(
  'FT.areaMeasurement',
  (polyline: DT.Polyline, zVector?: DT.Vector, yVector?: DT.Vector): DT.AreaMeasurement => {
    const basePts = polyline.value.points;

    if (basePts.length == 0) {
      return new DT.AreaMeasurement([], [], 0, 0, 0, 0, new THREE.Vector3(), new THREE.Vector3());
    }

    const params = areaMeasurementParams(basePts, polyline.value.isClosed, 90, zVector?.value, yVector?.value);

    if (!params) {
      throw new Error('Unable to calculate Area Measurement');
    }

    const center = new THREE.Vector3(0, 0, 0).applyMatrix4(params.cs);
    const orientationZ = new THREE.Vector3(0, 0, 1).transformDirection(params.cs);
    const orientationY = new THREE.Vector3(0, 1, 0).transformDirection(params.cs);

    const transformFrom = params.cs;
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    for (const pt of params.splinePts) {
      bBox.expandByPoint(pt.clone().applyMatrix4(transformTo));
    }
    bBox.min.sub(new THREE.Vector3(0, 0, 10));
    bBox.max.add(new THREE.Vector3(0, 0, 10));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);

    return new DT.AreaMeasurement(
      params.basePts,
      params.splinePts,
      params.area,
      params.perimeter,
      params.dA,
      params.dP,
      center,
      orientationZ,
      orientationY,
      minCorner,
      maxCorner,
      params.lMin,
      params.lMax,
      params.lPerp,
      params.dMean,
      params.lX,
      params.lY
    );
  }
);

export const areaMeasurementRef = MakeFunctor(
  'FT.areaMeasurementRef',
  (polyline: DT.Polyline, referenceMeas: DT.AreaMeasurement): DT.AreaMeasurement => {
    const basePts = polyline.value.points;

    const params = areaMeasurementParams(
      basePts,
      polyline.value.isClosed,
      90,
      referenceMeas.normal,
      referenceMeas.orientationY,
      referenceMeas.center
    );

    if (!params) {
      throw new Error('Unable to calculate Area Measurement');
    }

    const center = new THREE.Vector3(0, 0, 0).applyMatrix4(params.cs);
    const orientationZ = new THREE.Vector3(0, 0, 1).transformDirection(params.cs);
    const orientationY = new THREE.Vector3(0, 1, 0).transformDirection(params.cs);

    return new DT.AreaMeasurement(
      params.basePts,
      params.splinePts,
      params.area,
      params.perimeter,
      params.dA,
      params.dP,
      center,
      orientationZ,
      orientationY,
      referenceMeas.minCorner?.clone().sub(referenceMeas.center).add(center),
      referenceMeas.maxCorner?.clone().sub(referenceMeas.center).add(center),
      params.lMin,
      params.lMax,
      params.lPerp,
      params.dMean,
      params.lX,
      params.lY
    );
  }
);

function _splineMeasurementParams(
  basePts: Array<THREE.Vector3>,
  isClosed: boolean
): { splinePts: Array<THREE.Vector3>; length: number; center: THREE.Vector3; normal: THREE.Vector3 } | undefined {
  if (basePts.length == 0) {
    return undefined;
  }

  const splinePts = getCatmullRomCurve3Points(basePts, isClosed, 5, CatmullRomCurve3Type.Centripetal);

  let length = isClosed ? splinePts[0].distanceTo(splinePts[splinePts.length - 1]) : 0;
  for (let i = 1, sz = splinePts.length; i < sz; ++i) {
    length += splinePts[i].distanceTo(splinePts[i - 1]);
  }

  const origin = centroid(basePts);
  const orientationZ = unitNormal(origin, basePts, isClosed).normalize();

  return {
    splinePts: splinePts,
    length: length,
    center: origin,
    normal: orientationZ
  };
}

export const splineMeasurement = MakeFunctor('FT.splineMeasurement', (polyline: DT.Polyline): DT.SplineMeasurement => {
  const basePts = polyline.value.points;

  const params = _splineMeasurementParams(basePts, polyline.value.isClosed);

  if (!params) {
    throw new Error('Unable to calculate Spline Measurement');
  }

  return new DT.SplineMeasurement(
    params.splinePts,
    polyline.value.isClosed,
    params.length,
    params.center,
    params.normal
  );
});

export const makeSpline = MakeFunctor('FT.makeSpline', (poly: DT.Polyline, area: DT.AreaMeasurement): DT.Spline => {
  const basePts = poly.value.points;
  const isClosed = poly.value.isClosed;

  const splinePts = getCatmullRomCurve3Points(basePts, isClosed, 5, CatmullRomCurve3Type.Centripetal);

  return new DT.Spline({
    basePts: basePts,
    splinePts: splinePts,
    isClosed: isClosed,
    center: area.center,
    normal: area.normal
  });
});

export const closedPolyline = MakeFunctor('FT.closeSpline', (poly: DT.Polyline): DT.Polyline => {
  return new DT.Polyline({ points: poly.value.points, isClosed: true });
});

export const openPolyline = MakeFunctor('FT.openPolyline', (poly: DT.Polyline): DT.Polyline => {
  return new DT.Polyline({ points: poly.value.points, isClosed: false });
});

export const getLMin = MakeFunctor('FT.getLMin', (meas: DT.AreaMeasurement): DT.Line => {
  if (!meas.lMin) return new DT.Line(new THREE.Line3());
  return new DT.Line(meas.lMin);
});

export const getLMax = MakeFunctor('FT.getLMax', (meas: DT.AreaMeasurement): DT.Line => {
  if (!meas.lMax) return new DT.Line(new THREE.Line3());
  return new DT.Line(meas.lMax);
});

export const getAreaMeasurementCenter = MakeFunctor(
  'FT.getAreaMeasurementCenter',
  (meas: DT.AreaMeasurement): DT.Point => {
    return new DT.Point(meas.center.clone());
  }
);

export const getAreaMeasurementPlane = MakeFunctor(
  'FT.getAreaMeasurementPlane',
  (meas: DT.AreaMeasurement): DT.Plane => {
    return new DT.Plane({
      origin: meas.center.clone(),
      planeEq: new THREE.Plane().setFromNormalAndCoplanarPoint(meas.normal, meas.center)
    });
  }
);

export const getAreaMeasurementPerimeter = MakeFunctor(
  'FT.getAreaMeasurementPerimeter',
  (meas: DT.AreaMeasurement): DT.NumberT => {
    return new DT.NumberT(meas.perimeter);
  }
);

export const getAreaMeasurementPerimeterDerivedDiameter = MakeFunctor(
  'FT.getAreaMeasurementPerimeterDerivedDiameter',
  (meas: DT.AreaMeasurement): DT.NumberT => {
    return new DT.NumberT(meas.dP);
  }
);

export const getAreaMeasurementMinDiameter = MakeFunctor(
  'FT.getAreaMeasurementMinDiameter',
  (meas: DT.AreaMeasurement): DT.NumberT => {
    if (meas.dMin === undefined) {
      throw new Error('Undefined diameter');
    }
    return new DT.NumberT(meas.dMin);
  }
);

export const getAreaMeasurementMaxDiameter = MakeFunctor(
  'FT.getAreaMeasurementMaxDiameter',
  (meas: DT.AreaMeasurement): DT.NumberT => {
    if (meas.dMax === undefined) {
      throw new Error('Undefined diameter');
    }
    return new DT.NumberT(meas.dMax);
  }
);

export const getAreaMeasurementYDiameter = MakeFunctor(
  'FT.getAreaMeasurementYDiameter',
  (meas: DT.AreaMeasurement): DT.NumberT => {
    if (meas.dY === undefined) {
      throw new Error('Undefined diameter');
    }
    return new DT.NumberT(meas.dY);
  }
);

export const getAreaMeasurementXDiameter = MakeFunctor(
  'FT.getAreaMeasurementXDiameter',
  (meas: DT.AreaMeasurement): DT.NumberT => {
    if (meas.dX === undefined) {
      throw new Error('Undefined diameter');
    }
    return new DT.NumberT(meas.dX);
  }
);

export const triangleSurfaceFromMesh = MakeFunctor(
  'FT.triangleSurfaceFromMesh',
  (mesh: DT.Mesh): DT.MatTriangleSurface => {
    return new DT.MatTriangleSurface(createTriangleSurface(mesh.value, true));
  }
);

export const makeTriangleSurfaceVector = MakeFunctor(
  'FT.makeTriangleSurfaceVector',
  (...surfaces: Array<DT.MatTriangleSurface>): DT.MatVectorTriangleSurface => {
    const ret = new MatSDK.VectorTriangleSurface();
    for (const surf of surfaces) ret.push_back(surf.value);

    return new DT.MatVectorTriangleSurface(ret);
  }
);

export const meshFromTriangleSurface = MakeFunctor(
  'FT.meshFromTriangleSurface',
  (triangles: DT.MatTriangleSurface): DT.Mesh => {
    const geometry = createBufferGeometry(triangles.value);
    return new DT.Mesh(geometry);
  }
);

export const buildTriangleTree = MakeFunctor(
  'FT.buildTriangleTree',
  (surface: DT.MatTriangleSurface): DT.MatKDTree3d => {
    const tree = MatSDK.buildTriangleTree(surface.value);
    return new DT.MatKDTree3d(tree);
  }
);

export const buildEdgeTree = MakeFunctor('FT.buildEdgeTree', (lines: DT.MatLineSegments): DT.MatKDTree3d => {
  const tree = MatSDK.buildEdgeTree(lines.value);
  return new DT.MatKDTree3d(tree);
});

export const buildPolylinesTree = MakeFunctor(
  'FT.buildPolylinesTree',
  (polyline: DT.MatPolylinesVector): DT.MatVectorKDTree3d => {
    const tree = MatSDK.buildPolylinesTree(polyline.value);
    return new DT.MatVectorKDTree3d(tree);
  }
);

export const buildSurfaceGrid = MakeFunctor('FT.buildSurfaceGrid', (surface: DT.MatTriangleSurface): DT.MatGrid => {
  return new DT.MatGrid(new MatSDK.Grid(surface.value, MatSDK.optimalCell(surface.value, 0.1)));
});

export const matLineSegmentsFromPolyline = MakeFunctor(
  'FT.matLineSegmentsFromPolyline',
  (polyline: DT.Polyline): DT.MatLineSegments => {
    const poly = polyline.value;
    const points = getCatmullRomCurve3Points(poly.points, poly.isClosed, 5, CatmullRomCurve3Type.Centripetal);
    const vert: Array<number> = [];
    for (const pt of points) vert.push(pt.x, pt.y, pt.z);
    const seg: Array<number> = [];
    for (let i = 1; i < points.length; ++i) seg.push(i - 1, i);
    if (poly.isClosed) seg.push(points.length - 1, 0);
    return new DT.MatLineSegments(new MatSDK.LineSegments().setFromRaw(vert, seg));
  }
);

export const matLineSegmentsFromSpline = MakeFunctor(
  'FT.matLineSegmentsFromSpline',
  (spline: DT.Spline): DT.MatLineSegments => {
    const points = spline.value.splinePts;
    const vert: Array<number> = [];
    for (const pt of points) vert.push(pt.x, pt.y, pt.z);
    const seg: Array<number> = [];
    for (let i = 1; i < points.length; ++i) seg.push(i - 1, i);
    if (spline.value.isClosed) seg.push(points.length - 1, 0);
    return new DT.MatLineSegments(new MatSDK.LineSegments().setFromRaw(vert, seg));
  }
);

export const lineSegmentsMeshFromMatLineSegments = MakeFunctor(
  'FT.lineSegmentsMeshFromMatLineSegments',
  (segments: DT.MatLineSegments): DT.LineSegmentsMesh => {
    const geometry = createBufferGeometryFromLineSegments(segments.value);
    return new DT.LineSegmentsMesh(geometry);
  }
);

export const lineSegmentsMeshFromMatPolylinesVector = MakeFunctor(
  'FT.lineSegmentsMeshFromMatPolylinesVector',
  (polylines: DT.MatPolylinesVector): DT.LineSegmentsMesh => {
    const geometry = createBufferGeometryFromPolylinesVector(polylines.value);
    return new DT.LineSegmentsMesh(geometry);
  }
);

export const polylineMeshFromMatPolyline = MakeFunctor(
  'FT.polylineMeshFromMatPolyline',
  (polyline: DT.MatPolyline): DT.PolylineMesh => {
    const geometry = createBufferGeometryFromPolyline(polyline.value);
    return new DT.PolylineMesh(geometry);
  }
);

export const polylineMeshFromPolyline = MakeFunctor(
  'FT.polylineMeshFromPolyline',
  (polyline: DT.Polyline): DT.PolylineMesh => {
    const geometry = createBufferGeometryFromPointsCollection(polyline.value.points, polyline.value.isClosed);
    return new DT.PolylineMesh(geometry);
  }
);

export const polylineFromMatPolyline = MakeFunctor(
  'FT.polylineFromMatPolyline',
  (polyline: DT.MatPolyline): DT.Polyline => {
    const points = polyline.value.getRawPoints();
    const pts = new Array<THREE.Vector3>(points.length / 3);
    for (let i = 0, j = 0; i < points.length; i += 3, j++) {
      pts[j] = new THREE.Vector3().fromArray(points, i);
    }
    return new DT.Polyline({ points: pts, isClosed: polyline.value.closed });
  }
);

export const matPolylinesVectorFromPolylinesVector = MakeFunctor(
  'FT.matPolylinesVectorFromPolylinesVector',
  (vec: DT.PolylinesVector): DT.MatPolylinesVector => {
    const ret = new MatSDK.VectorPolyline3d();
    vec.value.polylines.forEach((polyline) => {
      const coord: Array<number> = [];
      for (let i = 0, j = 0; i < polyline.value.points.length; i++, j += 3) {
        coord.push(polyline.value.points[i].x, polyline.value.points[i].y, polyline.value.points[i].z);
      }

      const p = new MatSDK.Polyline3d();
      p.setFromRaw(coord, polyline.value.isClosed);
      ret.push_back(p);
    });

    return new DT.MatPolylinesVector(ret);
  }
);

export function defaultTuneFlag(): DT.TuneFlag {
  return new DT.TuneFlag(DT.ETuneFlag.DEFAULT);
}

export function emptyPolyline(): DT.Polyline {
  return new DT.Polyline({ points: [], isClosed: false });
}

export function emptyPoint(): DT.Point {
  return new DT.Point(new THREE.Vector3());
}

export function emptyVector(): DT.Vector {
  return new DT.Vector(new THREE.Vector3());
}

export function identityTransformMatrix(): DT.TransformMatrix {
  return new DT.TransformMatrix(new THREE.Matrix4());
}

export function emptyMeshDescriptor(): DT.StringT {
  return new DT.StringT('<empty>');
}

export function emptyPlane(): DT.Plane {
  return new DT.Plane({
    origin: new THREE.Vector3(),
    planeEq: new THREE.Plane()
  });
}

export const planeFromThreePoints = MakeFunctor(
  'FT.planeFromThreePoints',
  (p1: DT.Point, p2: DT.Point, p3: DT.Point, dir?: DT.Vector): DT.Plane => {
    const planeEq = new THREE.Plane().setFromCoplanarPoints(p1.value, p2.value, p3.value);
    if (dir && planeEq.normal.dot(dir.value) < 0) {
      planeEq.negate();
    }
    const origin = new THREE.Triangle(p1.value, p2.value, p3.value).getMidpoint(new THREE.Vector3());
    return new DT.Plane({ planeEq: planeEq, origin: origin });
  }
);

export const planeFromNormalAndOrigin = MakeFunctor(
  'FT.planeFromNormalAndOrigin',
  (n: DT.Vector, o: DT.Point): DT.Plane => {
    const planeEq = new THREE.Plane().setFromNormalAndCoplanarPoint(n.value.clone().normalize(), o.value);
    return new DT.Plane({ planeEq: planeEq, origin: o.value });
  }
);

export const translatePlane = MakeFunctor('FT.translatePlane', (pl: DT.Plane, dist: DT.NumberT): DT.Plane => {
  const translation = pl.value.planeEq.normal.clone().normalize().multiplyScalar(dist.value);
  const planeEq = pl.value.planeEq.clone().translate(translation);
  const origin = pl.value.origin.clone().add(translation);
  return new DT.Plane({ planeEq: planeEq, origin: origin });
});

export const lineBetweenPoints = MakeFunctor(
  'FT.lineBetweenPoints',
  (p1: DT.Point, p2: DT.Point, dir?: DT.Vector): DT.Line => {
    if (dir) {
      const vec = p2.value.clone().sub(p1.value);
      if (vec.dot(dir.value) < 0) {
        return new DT.Line(new THREE.Line3(p2.value, p1.value));
      }
    }
    return new DT.Line(new THREE.Line3(p1.value, p2.value));
  }
);

export const vectorFromPoints = MakeFunctor(
  'FT.vectorFromPoints',
  (p1: DT.Point, p2: DT.Point, dir?: DT.Vector): DT.Vector => {
    const vec = p2.value.clone().sub(p1.value);
    if (dir && vec.dot(dir.value) < 0) {
      vec.negate();
    }
    return new DT.Vector(vec);
  }
);

export const unitVectorFromPoints = MakeFunctor(
  'FT.unitVectorFromPoints',
  (p1: DT.Point, p2: DT.Point, dir?: DT.Vector): DT.Vector => {
    const vec = p2.value.clone().sub(p1.value);
    if (dir && vec.dot(dir.value) < 0) {
      vec.negate();
    }
    vec.normalize();
    return new DT.Vector(vec);
  }
);

export const crossProduct = MakeFunctor('FT.crossProduct', (v1: DT.Vector, v2: DT.Vector): DT.Vector => {
  return new DT.Vector(v1.value.clone().cross(v2.value));
});

export const cloneTransformMatrix = MakeFunctor(
  'FT.cloneTransformMatrix',
  (value: DT.TransformMatrix): DT.TransformMatrix => {
    return new DT.TransformMatrix(value.value.clone());
  }
);

export const reference = MakeFunctor('FT.reference', <T, U extends IValue & IWrapper<T>>(value: U): U => {
  return value;
});

export const makeRoiForVerticalLine = MakeFunctor(
  'FT.makeRoiForVerticalLine',
  (line: DT.Line, zVector: DT.Vector, yVector: DT.Vector): DT.RegionOfInterest => {
    const location: DT.RegionOfInterestT = {
      ptO: line.value.start.clone(),
      dirZ: new THREE.Vector3(),
      dirY: line.value.delta(new THREE.Vector3()).normalize(),
      minCorner: new THREE.Vector3(),
      maxCorner: new THREE.Vector3()
    };
    if (location.dirY.dot(yVector.value) < 0) {
      location.dirY.negate();
    }
    const dirX = location.dirY.clone().cross(zVector.value).normalize();
    location.dirZ = dirX.clone().cross(location.dirY).normalize();
    const transformFrom = makeTransformation(location.ptO, dirX, location.dirY, location.dirZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(line.value.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(line.value.end.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(10, 0, 10));
    bBox.max.add(new THREE.Vector3(10, 0, 10));
    location.minCorner = bBox.min.applyMatrix4(transformFrom);
    location.maxCorner = bBox.max.applyMatrix4(transformFrom);
    return new DT.RegionOfInterest(location);
  }
);

export const makeRoiForHorizontalLine = MakeFunctor(
  'FT.makeRoiForHorizontalLine',
  (line: DT.Line, zVector: DT.Vector, yVector: DT.Vector): DT.RegionOfInterest => {
    const location: DT.RegionOfInterestT = {
      ptO: line.value.start.clone(),
      dirZ: new THREE.Vector3(),
      dirY: new THREE.Vector3(),
      minCorner: new THREE.Vector3(),
      maxCorner: new THREE.Vector3()
    };
    const dir = line.value.delta(new THREE.Vector3()).normalize();
    location.dirY.copy(dir).cross(zVector.value).normalize();
    location.dirZ.copy(location.dirY).cross(dir).normalize();
    if (location.dirY.dot(yVector.value) < 0) {
      location.dirY.negate();
    }
    const transformFrom = makeTransformation(location.ptO, dir, location.dirY, location.dirZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(line.value.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(line.value.end.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 10, 10));
    bBox.max.add(new THREE.Vector3(0, 10, 10));
    location.minCorner.copy(bBox.min).applyMatrix4(transformFrom);
    location.maxCorner.copy(bBox.max).applyMatrix4(transformFrom);
    return new DT.RegionOfInterest(location);
  }
);

export const makeRoiForMesh = MakeFunctor(
  'FT.makeRoiForMesh',
  (mesh: DT.Mesh, yVec: DT.Vector, zVec: DT.Vector): DT.RegionOfInterest => {
    const location: DT.RegionOfInterestT = {
      ptO: new THREE.Vector3(),
      dirZ: new THREE.Vector3(),
      dirY: new THREE.Vector3(),
      minCorner: new THREE.Vector3(),
      maxCorner: new THREE.Vector3()
    };
    mesh.value.computeBoundingBox();
    if (!mesh.value.boundingBox) {
      throw new Error('Failed to compute bounding box for mesh');
    }
    const bbox = mesh.value.boundingBox;
    location.ptO = bbox.getCenter(new THREE.Vector3());
    location.dirZ.copy(zVec.value);
    location.dirY.copy(yVec.value);
    location.minCorner = bbox.min;
    location.maxCorner = bbox.max;
    return new DT.RegionOfInterest(location);
  }
);

export const makeDistanceMeasurement = MakeFunctor(
  'FT.makeDistanceMeasurement',
  (dist: DT.NumberT, line: DT.Line, roi?: DT.RegionOfInterest, criteria?: DT.CheckCriteria): DT.DistanceMeasurement => {
    return new DT.DistanceMeasurement(dist.value, line?.value.clone(), roi?.value, criteria?.value);
  }
);

export const getDistanceMeasurementValue = MakeFunctor(
  'FT.getDistanceMeasurementValue',
  (meas: DT.DistanceMeasurement): DT.NumberT => {
    return new DT.NumberT(meas.distance);
  }
);

export const makeValueMeasurement = MakeFunctor(
  'FT.makeValueMeasurement',
  (value: DT.NumberT, criteria?: DT.CheckCriteria): DT.ValueMeasurement => {
    return new DT.ValueMeasurement(value.value, criteria?.value);
  }
);

export const getValueMeasurementValue = MakeFunctor(
  'FT.getValueMeasurementValue',
  (meas: DT.ValueMeasurement): DT.NumberT => {
    return new DT.NumberT(meas.value);
  }
);

export const loadDracoMeshAsync = MakeAsyncFunctor(
  'FT.loadDracoMeshAsync',
  async (descriptor: DT.StringT): Promise<DT.Mesh> => {
    return new DT.Mesh(await loadDracoAsBufferGeometry(descriptor.value));
  }
);

export const isMeshNotEmpty = MakeFunctor('FT.isMeshNotEmpty', (mesh: DT.Mesh): DT.BooleanT => {
  return new DT.BooleanT(!!mesh.value.index && mesh.value.index.array.length != 0);
});

export const availableManufacturers = MakeFunctor(
  'FT.availableManufacturers',
  (instr: DT.InstrumentationLibrary): DT.IdentifiersList => {
    return new DT.IdentifiersList(instr.getAvailableManufacturers());
  }
);

export const loadInstrumentationLibraryAsync = MakeAsyncFunctor(
  'FT.loadInstrumentationLibraryAsync',
  async (url: DT.StringT): Promise<DT.InstrumentationLibrary> => {
    const modelsProvider = new InstrumentationModelsProvider();
    const instrProvider = new InstrumentationLoader();
    const instr = await instrProvider.loadInstrumentationLibraryAsync(url.value);
    return new DT.InstrumentationLibrary(instr as Instrumentation, modelsProvider);
  }
);

export const getDeviceModelAsync = MakeAsyncFunctor(
  'FT.getDeviceModelAsync',
  async <TDevice extends DT.IDevice>(device: TDevice): Promise<DT.Mesh> => {
    const model = await device.getModelAsync();
    return new DT.Mesh(model);
  }
);

export const getPreferredManufacturer = MakeFunctor(
  'FT.getPreferredManufacturer',
  (instr: DT.InstrumentationLibrary): DT.StringT => {
    return new DT.StringT(instr.instrumentation.preferredManufacturer);
  }
);

export const getAvailableManufacturers = MakeFunctor(
  'FT.getAvailableManufacturers',
  (instr: DT.InstrumentationLibrary): DT.IdentifiersList => {
    return new DT.IdentifiersList(instr.getAvailableManufacturers());
  }
);

export const getPreferredSystem = MakeFunctor('FT.getPreferredSystem', (manuf: DT.IManufacturer): DT.StringT => {
  return new DT.StringT(manuf.getPreferredSystemName());
});

export const getAvailableSystems = MakeFunctor(
  'FT.getAvailableSystems',
  (manuf: DT.IManufacturer): DT.IdentifiersList => {
    return new DT.IdentifiersList(manuf.getAvailableSystemsNames());
  }
);

export const directionOfLine = MakeFunctor('FT.directionOfLine', (ln: DT.Line): DT.Vector => {
  return new DT.Vector(ln.value.delta(new THREE.Vector3()));
});

export const perpendicularToLines = MakeFunctor('FT.perpendicularToLines', (ln1: DT.Line, ln2: DT.Line): DT.Vector => {
  return new DT.Vector(ln1.value.delta(new THREE.Vector3()).cross(ln2.value.delta(new THREE.Vector3())));
});

export const perpendicularToLine = MakeFunctor(
  'FT.perpendicularToLine',
  (ln: DT.Line, normal: DT.Vector): DT.Vector => {
    return new DT.Vector(normal.value.clone().cross(ln.value.delta(new THREE.Vector3())));
  }
);

export const getMeasurementROI = MakeFunctor('FT.getMeasurementROI', (meas: DT.ILocatable): DT.RegionOfInterest => {
  const location = meas.getLocation();
  if (!location) {
    throw new Error('Location is expected to be existent for this measurement');
  }
  return new DT.RegionOfInterest(location);
});

export const makeOrientationTop = MakeFunctor(
  'FT.makeOrientationTop',
  (top: DT.Vector, dir: DT.Vector): DT.Orientation => {
    return new DT.Orientation({
      top: top.value.clone().normalize(),
      dir: top.value.clone().cross(dir.value).cross(top.value).normalize()
    });
  }
);

export const makeOrientationDir = MakeFunctor(
  'FT.makeOrientationDir',
  (top: DT.Vector, dir: DT.Vector): DT.Orientation => {
    return new DT.Orientation({
      top: dir.value.clone().cross(top.value).cross(dir.value).normalize(),
      dir: dir.value.clone().normalize()
    });
  }
);

export const normalOf = MakeFunctor('FT.normalOf', (pl: DT.Plane): DT.Vector => {
  return new DT.Vector(pl.value.planeEq.normal.clone());
});

export const invertVector = MakeFunctor('FT.invertVector', (v: DT.Vector): DT.Vector => {
  return new DT.Vector(v.value.clone().negate());
});

export const subtractSurfaces = MakeFunctor(
  'FT.subtractSurfaces',
  (toSubtractFrom: DT.MatTriangleSurface, toBeSubtracted: DT.MatTriangleSurface): DT.MatTriangleSurface => {
    return new DT.MatTriangleSurface(MatSDK.subtract(toSubtractFrom.value, toBeSubtracted.value));
  }
);

export const combineMatPolylines = MakeFunctor(
  'FT.combineMatPolylines',
  (...args: Array<DT.MatPolyline>): DT.MatPolylinesVector => {
    const ret = new MatSDK.VectorPolyline3d();
    args.forEach((element) => ret.push_back(element.value));
    return new DT.MatPolylinesVector(ret);
  }
);

export const mergeMatPolylinesVector = MakeFunctor(
  'FT.mergeMatPolylinesVector',
  (polylines: DT.MatPolylinesVector): DT.MatLineSegments => {
    return new DT.MatLineSegments(MatSDK.mergePolylines(polylines.value));
  }
);

export const lineSegmentsFromPolylinesVector = MakeFunctor(
  'FT.lineSegmentsFromPolylinesVector',
  (polylines: DT.MatPolylinesVector): DT.MatLineSegments => {
    const vertices = [];
    const segments = [];
    for (let i = 0; i < polylines.value.size(); i++) {
      const polyline = polylines.value.access(i);
      const pts = polyline.accessPoints();
      for (let j = 0; j < pts.size() - 1; j++) {
        vertices.push(...pts.access(j).toArray());
        vertices.push(...pts.access(j + 1).toArray());
        segments.push(vertices.length / 3 - 2, vertices.length / 3 - 1);
      }
    }
    return new DT.MatLineSegments(new MatSDK.LineSegments().setFromRaw(vertices, segments));
  }
);

export const projectPointOnPlane = MakeFunctor('FT.projectPointOnPlane', (pt: DT.Point, pl: DT.Plane): DT.Point => {
  return new DT.Point(pl.value.planeEq.projectPoint(pt.value, new THREE.Vector3()));
});

export const negatePlane = MakeFunctor('FT.negatePlane', (pl: DT.Plane): DT.Plane => {
  return new DT.Plane({
    planeEq: pl.value.planeEq.clone().negate(),
    origin: pl.value.origin.clone()
  });
});

export const middleBetweenPoints = MakeFunctor('FT.middleBetweenPoints', (pt1: DT.Point, pt2: DT.Point): DT.Point => {
  return new DT.Point(pt1.value.clone().add(pt2.value).divideScalar(2));
});

export const isGreaterThan = MakeFunctor(
  'FT.isGreaterThan',
  (value: DT.NumberT, greaterThan: DT.NumberT): DT.BooleanT => {
    return new DT.BooleanT(value.value > greaterThan.value);
  }
);

export const isLessThan = MakeFunctor('FT.isLessThan', (value: DT.NumberT, lessThan: DT.NumberT): DT.BooleanT => {
  return new DT.BooleanT(value.value < lessThan.value);
});

export const isWithinRange = MakeFunctor(
  'FT.isWithinRange',
  (value: DT.NumberT, greaterThan: DT.NumberT, lessThan: DT.NumberT): DT.BooleanT => {
    return new DT.BooleanT(value.value > greaterThan.value && value.value < lessThan.value);
  }
);

export const makeGreaterThanCriteriaRange = MakeFunctor(
  'FT.makeGreaterThanCriteriaRange',
  (greaterThan: DT.NumberT): DT.CriteriaRange => {
    return new DT.CriteriaRange({
      greaterThan: greaterThan.value,
      lessThan: undefined
    });
  }
);

export const makeLessThanCriteriaRange = MakeFunctor(
  'FT.makeLessThanCriteriaRange',
  (lessThan: DT.NumberT): DT.CriteriaRange => {
    return new DT.CriteriaRange({
      greaterThan: undefined,
      lessThan: lessThan.value
    });
  }
);

export const makeWithinCriteriaRange = MakeFunctor(
  'FT.makeWithinCriteriaRange',
  (greaterThan: DT.NumberT, lessThan: DT.NumberT): DT.CriteriaRange => {
    return new DT.CriteriaRange({
      greaterThan: greaterThan.value,
      lessThan: lessThan.value
    });
  }
);

export const makeYesNoCriteria = MakeFunctor(
  'FT.makeYesNoCriteria',
  (value: DT.NumberT, yesRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks yesRange and defaults to 'no'
    return new DT.CheckCriteria(_makeCheckCriteria(value.value, DT.CheckStatusT.No, yesRange.value));
  }
);

export const makeYesMaybeCriteria = MakeFunctor(
  'FT.makeYesMaybeCriteria',
  (value: DT.NumberT, yesRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks yesRange and defaults to 'maybe'
    return new DT.CheckCriteria(_makeCheckCriteria(value.value, DT.CheckStatusT.Maybe, yesRange.value));
  }
);

export const makeMaybeYesCriteria = MakeFunctor(
  'FT.makeMaybeYesCriteria',
  (value: DT.NumberT, maybeRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks maybeRange and defaults to 'yes'
    return new DT.CheckCriteria(_makeCheckCriteria(value.value, DT.CheckStatusT.Yes, undefined, maybeRange.value));
  }
);

export const makeNoYesCriteria = MakeFunctor(
  'FT.makeNoYesCriteria',
  (value: DT.NumberT, noRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks noRange and defaults to 'yes'
    return new DT.CheckCriteria(
      _makeCheckCriteria(value.value, DT.CheckStatusT.Yes, undefined, undefined, noRange.value)
    );
  }
);

export const makeNoMaybeCriteria = MakeFunctor(
  'FT.makeNoMaybeCriteria',
  (value: DT.NumberT, noRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks noRange and defaults to 'maybe'
    return new DT.CheckCriteria(
      _makeCheckCriteria(value.value, DT.CheckStatusT.Maybe, undefined, undefined, noRange.value)
    );
  }
);

export const makeMaybeNoCriteria = MakeFunctor(
  'FT.makeMaybeNoCriteria',
  (value: DT.NumberT, maybeRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks maybeRange and defaults to 'no'
    return new DT.CheckCriteria(_makeCheckCriteria(value.value, DT.CheckStatusT.No, undefined, maybeRange.value));
  }
);

export const makeYesMaybeNoCriteria = MakeFunctor(
  'FT.makeYesMaybeNoCriteria',
  (value: DT.NumberT, yesRange: DT.CriteriaRange, maybeRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks yesRange and maybeRange and defaults to 'no'
    return new DT.CheckCriteria(_makeCheckCriteria(value.value, DT.CheckStatusT.No, yesRange.value, maybeRange.value));
  }
);

export const makeNoMaybeYesCriteria = MakeFunctor(
  'FT.makeNoMaybeYesCriteria',
  (value: DT.NumberT, noRange: DT.CriteriaRange, maybeRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks noRange and maybeRange and defaults to 'yes'
    return new DT.CheckCriteria(
      _makeCheckCriteria(value.value, DT.CheckStatusT.Yes, undefined, maybeRange.value, noRange.value)
    );
  }
);

export const makeYesNoMaybeCriteria = MakeFunctor(
  'FT.makeYesNoMaybeCriteria',
  (value: DT.NumberT, yesRange: DT.CriteriaRange, noRange: DT.CriteriaRange): DT.CheckCriteria => {
    // checks yesRange and maybeRange and defaults to 'no'
    return new DT.CheckCriteria(
      _makeCheckCriteria(value.value, DT.CheckStatusT.Maybe, yesRange.value, undefined, noRange.value)
    );
  }
);

export const getCheckStatus = MakeFunctor('FT.getCheckStatus', (value: DT.CheckCriteria): DT.CheckStatus => {
  return new DT.CheckStatus(value.value.status);
});

export const logicalAnd = MakeFunctor('FT.logicalAnd', (...values: Array<DT.BooleanT>): DT.BooleanT => {
  return new DT.BooleanT(values.every((value) => value.value));
});

export const logicalOr = MakeFunctor('FT.logicalOr', (...values: Array<DT.BooleanT>): DT.BooleanT => {
  return new DT.BooleanT(values.some((value) => value.value));
});

export const addNumbers = MakeFunctor('FT.addNumbers', (value1: DT.NumberT, value2: DT.NumberT): DT.NumberT => {
  return new DT.NumberT(value1.value + value2.value);
});

export const subtractNumbers = MakeFunctor(
  'FT.subtractNumbers',
  (value1: DT.NumberT, value2: DT.NumberT): DT.NumberT => {
    return new DT.NumberT(value1.value - value2.value);
  }
);

export const multiplyNumbers = MakeFunctor(
  'FT.multiplyNumbers',
  (value1: DT.NumberT, value2: DT.NumberT): DT.NumberT => {
    return new DT.NumberT(value1.value * value2.value);
  }
);

export const divideNumbers = MakeFunctor('FT.divideNumbers', (value1: DT.NumberT, value2: DT.NumberT): DT.NumberT => {
  return new DT.NumberT(value1.value / value2.value);
});

export const negateNumber = MakeFunctor('FT.negateNumber', (value: DT.NumberT): DT.NumberT => {
  return new DT.NumberT(-value.value);
});

export const makePointsArray = MakeFunctor('FT.makePointsArray', (...points: Array<DT.Point>): DT.PointsArray => {
  return new DT.PointsArray(points.map((pt) => pt.value));
});
